import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, onMounted, PropType, Ref, ref, watch } from "vue";
import BusStopList from "@/components/line/BusStopList.vue";
import { IBusStop } from "@/domain/entities/busStop";
import { IFavoriteLine } from "@/domain/entities/favoriteLine";
import {
  IArrivalTimeOption,
  IBus,
  IBusLocation,
} from "@/domain/entities/lineInfo";
import { PROVIDE_KEY_BUS_LOCATIONS } from "@/types";
import useConvertTimeStrToMinutes from "@/use/useConvertTimeStrToMinutes";
import BusArrivalTimeSelect from "./BusArrivalTimeSelect.vue";
import { IArrivalTime } from "@/domain/entities/lineInfo/arrivalTime.types";
import useGetAccessToken from "@/use/useGetAccessToken";


const __default__ = {
  name: "LineInfoDetailSelectBusStop",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  busStops: {
    type: Object as PropType<Array<IBusStop>>,
    required: true,
  },
  selectedBus: {
    type: Object as PropType<IBusLocation>,
  },
  favoriteLine: {
    type: Object as PropType<IFavoriteLine>,
  },
  isInsideShuttle: {
    type: Boolean,
    default: false,
  },
  allBusesMap: {
    type: Object as PropType<Map<string, IBus>>,
  },
  isBackwardLine: {
    type: Boolean,
    default: false,
  },
  lineId: {
    type: String,
    required: true,
  },
  turnId: {
    type: String,
    required: true,
  },
  isBookable: {
    type: Boolean,
  },
  arrivalTimeOptions: {
    type: Array as PropType<IArrivalTimeOption[]>,
    default: () => [],
  },
  selectedArrivalTimeOption: {
    type: Object as PropType<IArrivalTimeOption>,
  },
  arrivalTimesForDisplay: {
    type: Array as PropType<IArrivalTime[]>,
    default: () => [],
  },
  shouldShowBusLocations: {
    type: Boolean,
    default: true,
  },
  shouldShowBusArrivalTimeSelect: {
    type: Boolean,
    default: true,
  },
  detourEndDtm: {
    type: String,
  },
},
  emits: ["onChangedBusStopSelection", "onClickBusStopLocation", "updateBusLocation", "onClickScheduleBtn", "onChangeArrivalTimeOption", "onSelectPastTimeOption", "onClickBubble", "onClickFavoriteStatusButton", "onClickBusIcon"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const emitOnClickFavoriteStatusButton = (busStop: IBusStop) => {
  emit("onClickFavoriteStatusButton", busStop);
};

const emitOnClickBusIcon = (busesImageExisted: IBus[]) => {
  emit("onClickBusIcon", busesImageExisted);
};

const busLocations = inject(PROVIDE_KEY_BUS_LOCATIONS) as Ref<IBusLocation[]>;

/** 로그인 여부 */
const isLoggedIn = computed(() => !!useGetAccessToken());

const selectComplete = ref(false);
const boardingBusStop = ref<IBusStop | undefined>(undefined);
const arrivalBusStop = ref<IBusStop | undefined>(undefined);

const emitOnClickScheduleBtn = (busStop: IBusStop) => {
  emit("onClickScheduleBtn", busStop);
};

const emitOnChangedArrivalTimeOption = (option: IArrivalTimeOption) => {
  emit("onChangeArrivalTimeOption", option);
};

const emitOnSelectPastTimeOption = () => {
  emit("onSelectPastTimeOption");
};

const emitOnClickBusStopLocation = (busStop: IBusStop) => {
  emit("onClickBusStopLocation", busStop);
};

const emitOnClickBubble = () => {
  emit("onClickBubble");
};

/** 배차가 존재하는 경우에만 busLocation 존재 */
const isAllocExisted = computed(() => busLocations.value.length > 0);

let clickEventOffsetY = -1;
const toggleSelectBusStop = ({
  busStop,
  selected,
  offsetY,
}: {
  busStop: IBusStop;
  selected: boolean;
  offsetY: number;
}) => {
  if (busStop.isGetOnStop) {
    boardingBusStop.value = selected ? busStop : undefined;
  } else {
    arrivalBusStop.value = selected ? busStop : undefined;
  }
  clickEventOffsetY = offsetY;
};

const refreshButton = ref<HTMLElement>();

const updateBusLocation = () => {
  refreshButton.value?.classList.add("rotate");
  setTimeout(() => {
    refreshButton.value?.classList.remove("rotate");
  }, 1000);
  emit("updateBusLocation");
};

watch(
  [boardingBusStop, arrivalBusStop],
  ([boardingBusStop, arrivalBusStop]) => {
    if (props.isInsideShuttle || !props.isBookable) return;

    selectComplete.value = !!boardingBusStop && !!arrivalBusStop;
    updateHeaderTitle();
    emit(
      "onChangedBusStopSelection",
      selectComplete.value,
      clickEventOffsetY,
      boardingBusStop,
      arrivalBusStop
    );
  }
);

const resetSelectedBusStops = () => {
  boardingBusStop.value = undefined;
  arrivalBusStop.value = undefined;
};

const headerTitle = ref("출발지를 선택하세요");
const setHeaderTitle = (title: string) => {
  headerTitle.value = title;
};

const updateHeaderTitle = () => {
  if (!selectComplete.value) {
    setHeaderTitle(
      boardingBusStop.value ? "도착지를 선택하세요" : "출발지를 선택하세요"
    );
    return;
  }
  const timeDiffMin =
    useConvertTimeStrToMinutes(arrivalBusStop.value!.arrivalTime) -
    useConvertTimeStrToMinutes(boardingBusStop.value!.departureTime);
  const requiredTimeHour = Math.trunc(timeDiffMin / 60);
  const requiredTimeMin = timeDiffMin % 60;
  let requiredTimeStr = requiredTimeHour > 0 ? `${requiredTimeHour}시간` : "";
  requiredTimeStr += requiredTimeMin > 0 ? ` ${requiredTimeMin}분` : "";

  setHeaderTitle(`소요시간 ${requiredTimeStr}`);
};

watch(
  () => props.favoriteLine,
  () => {
    if (!props.favoriteLine || props.isInsideShuttle) return;
    boardingBusStop.value = props.favoriteLine?.getOnBusStop;
    arrivalBusStop.value = props.favoriteLine?.getOffBusStop;
  }
);

onMounted(() => {
  if (props.isInsideShuttle || !props.isBookable) {
    setHeaderTitle("정류장 안내");
  }
});

__expose({ resetSelectedBusStops });

const __returned__ = { props, emit, emitOnClickFavoriteStatusButton, emitOnClickBusIcon, busLocations, isLoggedIn, selectComplete, boardingBusStop, arrivalBusStop, emitOnClickScheduleBtn, emitOnChangedArrivalTimeOption, emitOnSelectPastTimeOption, emitOnClickBusStopLocation, emitOnClickBubble, isAllocExisted, get clickEventOffsetY() { return clickEventOffsetY }, set clickEventOffsetY(v) { clickEventOffsetY = v }, toggleSelectBusStop, refreshButton, updateBusLocation, resetSelectedBusStops, headerTitle, setHeaderTitle, updateHeaderTitle, BusStopList, BusArrivalTimeSelect }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})